import { CategoryAutoComplete } from '../../../classifier/components/CategoryAutoComplete';
import { FormField, FormInput } from '../../../common/form';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Cascader, Flex } from 'antd';
import styled from 'styled-components';
import { colorBlue, colorGray } from '@atlas-frontend/design-tokens';
import {
  FieldValues,
  useFormContext,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { DefaultOptionType } from 'rc-select/lib/Select';
import { Padding, RequiredText } from '../../../common/components';
import { getAlgorithmSourceLabel } from '../utils';
import * as Icon from 'react-feather';
import {
  AlgorithmElementFunctionViewType,
  DataObjectDimensionType,
} from '../../types';
import translations from './AlgorithmConditionForm.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { AlgorithmConditionElementFunctionItems } from './AlgorithmConditionElementFunctionItems';
import { formatCodeName } from '../../../common/utils';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import { SQL_MAX_LENGTH } from '../../../common/constants';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-condition-form',
);
addTranslations(translations);

export interface AlgorithmConditionFormProps {
  name: string;
  label?: string;
  required?: boolean;
  rows?: number;
  alwaysShowActions?: boolean;
  viewType: AlgorithmElementFunctionViewType;
  sources?: AlgorithmSourceFormDataType[];
  mappings?: AlgorithmMappingQueryDtoType[];
}

interface SourceMappingOption extends DefaultOptionType {
  classifierId?: number;
  classifierCode?: string;
}

function replaceSelectionWithText(
  getValues: UseFormGetValues<FieldValues>,
  setValue: UseFormSetValue<any>,
  name: string,
  text?: string,
) {
  if (text) {
    const inputElement = document.getElementById(
      'algorithm-condition-' + name,
    ) as HTMLTextAreaElement;
    const selectionStart = inputElement.selectionStart;
    const value = getValues(name);
    setValue(
      name,
      value.slice(0, selectionStart) + text + value.slice(selectionStart),
      { shouldDirty: true },
    );
  }
}

export const useOutsideClick = (
  elementClasses: string[],
  setShowActionsBlock: Dispatch<SetStateAction<boolean | undefined>>,
) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isClickInside = (elementClasses || [])
        .flatMap((item) => document.getElementsByClassName(item)[0])
        .some((item) => item?.contains(event.target as Node));
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        !isClickInside
      ) {
        setShowActionsBlock(false);
      }
    };

    document.addEventListener('mouseup', handleClickOutside);

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [elementClasses, setShowActionsBlock]);

  return ref;
};

export const AlgorithmConditionForm: FC<AlgorithmConditionFormProps> = (
  props,
) => {
  const {
    name,
    label,
    required = false,
    rows = 3,
    alwaysShowActions = false,
    viewType,
    sources,
    mappings,
  } = props;
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const [sourceMappingText, setSourceMappingText] = useState<
    string | undefined
  >(undefined);
  const [categoryText, setCategoryText] = useState<string | undefined>(
    undefined,
  );
  const [classifierId, setClassifierId] = useState<number | undefined>(
    undefined,
  );
  const [classifierCode, setClassifierCode] = useState<string | undefined>(
    undefined,
  );
  const [showActionsBlock, setShowActionsBlock] = useState<boolean | undefined>(
    false,
  );

  const sourceMappingOptions = useMemo(() => {
    const options = [];
    options.push(
      ...(sources || []).map((source: AlgorithmSourceFormDataType) => {
        const sourceValue = source.alias
          ? source.alias
          : `${source.sourceCode}${
              source.numberOfPreviousPeriod
                ? source.numberOfPreviousPeriod > 0
                  ? `[T+${source.numberOfPreviousPeriod}]`
                  : `[T${source.numberOfPreviousPeriod}]`
                : ''
            }`;
        const children: { value: string; label: string }[] = [];
        if ('dataObjectDescription' in source && source.dataObjectDescription) {
          const sourceDimensions = (source?.dimensions || []).map((item) => ({
            value: item.code,
            label: formatCodeName(item),
            classifierId:
              item.dimensionTypeCode === DataObjectDimensionType.CLASSIFIER
                ? item.classifier?.id
                : '',
            classifierCode:
              item.dimensionTypeCode ===
              DataObjectDimensionType.SUBJECT_ATTRIBUTE
                ? item.code
                : '',
          }));

          const selfSource =
            source.dataObjectDescription.id ===
            dataObjectDescriptionVersion.dataObjectId;
          const sourceIndicators = (source?.indicators || [])
            .filter((item) => !item.intermediate || selfSource)
            .map((item) => ({
              value: item.code,
              label: formatCodeName(item),
            }));
          children.push(...sourceDimensions);
          children.push(...sourceIndicators);
        }
        return {
          value: sourceValue,
          label: getAlgorithmSourceLabel(source),
          children,
        };
      }),
    );
    options.push(
      ...(mappings || []).map((mapping: AlgorithmMappingQueryDtoType) => ({
        value: mapping.alias,
        label: mapping.alias,
      })),
    );
    return options;
  }, [sources, mappings]);

  const handleSourceMappingChange = useCallback(
    (
      value: (string | number | null)[],
      selectOptions: DefaultOptionType[] | DefaultOptionType[][],
    ) => {
      setSourceMappingText(value?.join('.'));
      setClassifierId(
        selectOptions
          ?.filter((item: SourceMappingOption) => item?.classifierId)
          .map((item: SourceMappingOption) => item?.classifierId)[0] ||
          undefined,
      );
      setClassifierCode(
        selectOptions
          ?.filter((item: SourceMappingOption) => item?.classifierCode)
          .map((item: SourceMappingOption) => item?.classifierCode)[0] ||
          undefined,
      );
    },
    [setSourceMappingText],
  );

  const replaceSelectionWithOperatorSourceMapping = useCallback(() => {
    replaceSelectionWithText(getValues, setValue, name, sourceMappingText);
  }, [sourceMappingText]);

  const handleCategoryChange = useCallback(
    (value: string) => {
      setCategoryText(value);
    },
    [setCategoryText],
  );

  const replaceSelectionWithTextCategory = useCallback(() => {
    replaceSelectionWithText(getValues, setValue, name, categoryText);
  }, [categoryText]);

  const elementClasses = [
    'source-mapping-select-popup-' + name,
    'category-select-popup-' + name,
  ];
  const ref = useOutsideClick(elementClasses, setShowActionsBlock);
  return (
    <div
      id="algorithm-condition-block"
      onFocus={() => setShowActionsBlock(true)}
      ref={ref}
    >
      {label && (
        <label htmlFor="algorithm-condition-block-label">
          <RequiredText required={required}>{label}</RequiredText>
        </label>
      )}
      <PaddingWrapper hidden={!(showActionsBlock || alwaysShowActions)}>
        <Flex vertical gap="middle">
          <AlgorithmConditionElementFunctionItems
            fieldName={name}
            viewType={viewType}
          />
          <Flex gap="middle">
            <Flex vertical style={{ width: '70%' }}>
              <label htmlFor="source-mapping-select-button">
                {t('source-mapping-select.label')}
              </label>
              <Flex>
                <Cascader
                  id="source-mapping-select"
                  showSearch
                  displayRender={(label, selectedOptions) =>
                    selectedOptions?.map((item) => item.value).join('.')
                  }
                  options={sourceMappingOptions}
                  onChange={handleSourceMappingChange}
                  classNames={{
                    popup: { root: 'source-mapping-select-popup-' + name },
                  }}
                  style={{ width: '90%' }}
                  expandTrigger="hover"
                  changeOnSelect
                />
                <SelectButtonWrapper
                  id="source-mapping-select-button"
                  type="default"
                  onClick={() => replaceSelectionWithOperatorSourceMapping()}
                >
                  <Icon.Check />
                </SelectButtonWrapper>
              </Flex>
            </Flex>
            <Flex vertical style={{ width: '30%' }}>
              <label htmlFor="category-select">
                {t('category-select.label')}
              </label>
              <Flex>
                <CategoryAutoComplete
                  classifierId={classifierId}
                  classifierCode={classifierCode}
                  disabled={!classifierCode && !classifierId}
                  styles={{
                    popup: {
                      root: {
                        width: '325px',
                      },
                    },
                  }}
                  id="category-select"
                  onChange={handleCategoryChange}
                  classNames={{
                    popup: {
                      root: 'category-select-popup-' + name,
                    },
                  }}
                  style={{ width: '90%' }}
                />
                <SelectButtonWrapper
                  id="category-select-button"
                  type="default"
                  onClick={() => replaceSelectionWithTextCategory()}
                >
                  <Icon.Check />
                </SelectButtonWrapper>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </PaddingWrapper>
      <FormField name={name}>
        <FormInput
          id={'algorithm-condition-' + name}
          name={name}
          rows={rows}
          maxLength={SQL_MAX_LENGTH}
          style={{ fontFamily: 'monospace', borderRadius: 0 }}
          onFocus={(e) => {
            const el = e.currentTarget;
            requestAnimationFrame(() => {
              if (el && el.isConnected) {
                el.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                  inline: 'nearest',
                });
              }
            });
          }}
        />
      </FormField>
    </div>
  );
};

export const PaddingWrapper = styled(Padding)`
  background-color: ${colorGray[1]};
  border-color: ${colorGray[3]};
`;

export const SelectButtonWrapper = styled(Button)`
  background-color: ${colorGray[1]};
  border-color: ${colorGray[3]};
  color: ${colorBlue[3]};
  border-radius: 0;
`;
